import React, { useState } from "react";
import { Search, SlidersHorizontal, MessageSquarePlus } from "lucide-react";

import { useNavigate } from "react-router-dom";
import { Conversation } from "../../conversation/types";

// ── Status helpers ────────────────────────────────────────────────────────────

const CHANNEL_ICON: Record<string, string> = {
  whatsapp: "bg-green-500",
  sms: "bg-blue-500",
  email: "bg-indigo-500",
  default: "bg-gray-400",
};

const getChannelColor = (conversation: Conversation) => {
  // Determine channel from title or other fields
  const title = conversation.title?.toLowerCase() || "";
  if (title.includes("wa") || title.startsWith("(in)") || title.startsWith("(out)")) {
    return CHANNEL_ICON.whatsapp;
  }
  return CHANNEL_ICON.default;
};


// ── ConversationsTab ──────────────────────────────────────────────────────────

interface ConversationsTabProps {
  // contactId: string;
  conversations: Conversation[];
  loading: boolean;
}

const ConversationsTab: React.FC<ConversationsTabProps> = ({
  conversations = [],
  loading,
}) => {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();



  const filtered = React.useMemo(() => {
    const ticketList = Array.isArray(conversations) ? conversations : [];
    if (!search) return ticketList;

    const q = search.toLowerCase();
    return ticketList.filter((t) => {
      return (
        (t.title || "").toLowerCase().includes(q) ||
        (t.last_message?.body || "").toLowerCase().includes(q)
      );
    });
  }, [conversations, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => navigate('/chats')} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3.5 py-2 rounded-lg transition-colors">
          <MessageSquarePlus size={14} />
          New Conversation
        </button>

        <div className="relative flex-1 min-w-[160px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500" />
          <input
            type="text"
            placeholder="Search Conversations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-500/30"
          />
        </div>

        <button className="flex items-center gap-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          <SlidersHorizontal size={13} />
          Filters
        </button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
          {search ? "No conversations match your search." : "No conversations yet."}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden divide-y divide-gray-50 dark:divide-gray-800">
          {filtered.map((c) => {
            const channelColor = getChannelColor(c);
            const lastMsgBody = c.last_message?.body || c.title || "—";
            const isTemplate = c.last_message?.type === "template";
            const displayBody = isTemplate
              ? c.title
              : lastMsgBody;

            return (
              <div
                key={c.id}
                className="flex items-start gap-3 px-4 py-4 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/10 transition-colors"
              >
                {/* Channel indicator */}
                <div
                  className={`rounded-full flex items-center justify-center shrink-0 mt-0.5 `}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12.5912 23.2584L12.5802 23.2604L12.5092 23.2954L12.4892 23.2994L12.4752 23.2954L12.4042 23.2604C12.3936 23.2571 12.3856 23.2588 12.3802 23.2654L12.3762 23.2754L12.3592 23.7034L12.3642 23.7234L12.3742 23.7364L12.4782 23.8104L12.4932 23.8144L12.5052 23.8104L12.6092 23.7364L12.6212 23.7204L12.6252 23.7034L12.6082 23.2764C12.6056 23.2658 12.5999 23.2598 12.5912 23.2584ZM12.8562 23.1454L12.8432 23.1474L12.6582 23.2404L12.6482 23.2504L12.6452 23.2614L12.6632 23.6914L12.6682 23.7034L12.6762 23.7104L12.8772 23.8034C12.8899 23.8068 12.8996 23.8041 12.9062 23.7954L12.9102 23.7814L12.8762 23.1674C12.8729 23.1554 12.8662 23.1481 12.8562 23.1454ZM12.1412 23.1474C12.1368 23.1448 12.1315 23.1439 12.1265 23.145C12.1215 23.1461 12.1171 23.1491 12.1142 23.1534L12.1082 23.1674L12.0742 23.7814C12.0749 23.7934 12.0806 23.8014 12.0912 23.8054L12.1062 23.8034L12.3072 23.7104L12.3172 23.7024L12.3212 23.6914L12.3382 23.2614L12.3352 23.2494L12.3252 23.2394L12.1412 23.1474Z" fill="#34A853" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.477 2 12C2 13.89 2.525 15.66 3.438 17.168L2.546 20.2C2.49478 20.3741 2.49141 20.5587 2.53624 20.7346C2.58107 20.9104 2.67245 21.0709 2.80076 21.1992C2.92907 21.3276 3.08958 21.4189 3.26542 21.4638C3.44125 21.5086 3.62592 21.5052 3.8 21.454L6.832 20.562C8.39074 21.5049 10.1782 22.0023 12 22C17.523 22 22 17.523 22 12C22 6.477 17.523 2 12 2ZM9.738 14.263C11.761 16.285 13.692 16.552 14.374 16.577C15.411 16.615 16.421 15.823 16.814 14.904C16.8632 14.7896 16.881 14.6641 16.8655 14.5405C16.85 14.417 16.8019 14.2998 16.726 14.201C16.178 13.501 15.437 12.998 14.713 12.498C14.5619 12.3932 14.3761 12.3512 14.1946 12.3806C14.0131 12.4101 13.8502 12.5088 13.74 12.656L13.14 13.571C13.1083 13.62 13.0591 13.6551 13.0025 13.6692C12.9459 13.6833 12.886 13.6754 12.835 13.647C12.428 13.414 11.835 13.018 11.409 12.592C10.983 12.166 10.611 11.6 10.402 11.219C10.3767 11.1705 10.3696 11.1145 10.3819 11.0611C10.3941 11.0078 10.425 10.9606 10.469 10.928L11.393 10.242C11.5252 10.1276 11.6106 9.96841 11.6328 9.79495C11.6549 9.62149 11.6123 9.44596 11.513 9.302C11.065 8.646 10.543 7.812 9.786 7.259C9.6881 7.18866 9.57369 7.14479 9.45385 7.13165C9.33402 7.11851 9.21282 7.13654 9.102 7.184C8.182 7.578 7.386 8.588 7.424 9.627C7.449 10.309 7.716 12.24 9.738 14.263Z" fill="#34A853" />
                  </svg>

                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {c.title || "Conversation"}:&nbsp;
                        <span className="font-medium text-gray-600 dark:text-gray-400 truncate">
                          {displayBody}
                        </span>
                      </p>
                      {c.last_message?.direction === "outbound" && (
                        <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5 truncate">
                          Agent: Let me check this for you
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">
                        {c.last_message_at
                          ? new Date(c.last_message_at).toLocaleDateString(
                            "en-US",
                            { month: "short", day: "numeric", year: "numeric" }
                          ) === new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                            ? "Today"
                            : new Date(c.last_message_at).toLocaleDateString(
                              "en-US",
                              { month: "short", day: "numeric", year: "numeric" }
                            )
                          : "—"}
                      </p>
                      <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-0.5">
                        {c.last_message_at
                          ? new Date(c.last_message_at).toLocaleTimeString(
                            "en-US",
                            { hour: "numeric", minute: "2-digit" }
                          )
                          : ""}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      window.dispatchEvent(
                        new CustomEvent("navigate-to-conversation", {
                          detail: {
                            conversationId: c?.id,
                            phoneNumber: c?.external_thread_id,
                            title: c?.title,
                          },
                        }),
                      );
                    }}

                    type="button"
                    className="text-xs text-indigo-500 dark:text-indigo-400 font-medium underline mt-1 inline-block"
                  >
                    View Conversation
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConversationsTab;